import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import 'react-quill/dist/quill.snow.css';
import './news.css';
import { createNewsPost } from "../../actions/NewsPages";

interface BodyImage {
    id: number;
    file: File | null;
    caption: string;
    portrait: boolean;
}

interface FormData {
    title: string;
    subtitle: string;
    year_posted: string;
    image: File | null;
    video: string;
    content: string;
    content_2: string;
    [key: string]: any;
    template: number;
}

function CreateNewsPost() {
    const location = useLocation();
    const navigate = useNavigate();

    if (!location.state?.user) {
        navigate('/login');
    }

    const user = location.state?.user;
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [errors, setErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [bodyImages, setBodyImages] = useState<BodyImage[]>([]);
    const [showIframeHelp, setShowIframeHelp] = useState(false);

    const [formData, setFormData] = useState<FormData>({
        title: "",
        subtitle: "",
        year_posted: "",
        image: null,
        video: "",
        content: "",
        content_2: "",
        template: 1,
    });

    const ReactQuill = require('react-quill');
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean'],
        ],
    };

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(""), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === "file") {
            const fileInput = e.target as HTMLInputElement;
            const file = fileInput.files?.[0] || null;
            setFormData(prev => ({ ...prev, [name]: file }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleQuillChange = (value: string, name: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Check if Quill editor is effectively empty
    const isQuillEmpty = (html: string) => {
        if (!html) return true;
        const stripped = html.replace(/<p>(&nbsp;|\s|<br>)*<\/p>/gi, "").trim();
        return stripped === "";
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>, isDraft: boolean = false) => {
        e.preventDefault();
        setErrors([]);
        setSuccessMessage("");

        if (!isDraft) {
            const missingFields: string[] = [];
            if (!formData.title.trim()) missingFields.push("Title");
            if (!formData.subtitle.trim()) missingFields.push("Subtitle");
            if (!formData.year_posted.trim()) missingFields.push("Date");
            if (!formData.content.trim()) missingFields.push("Content");

            if (missingFields.length > 0) {
                alert(`Please fill in the following required field(s):\n- ${missingFields.join("\n- ")}`);
                return;
            }
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('draft', isDraft.toString());

            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    let value = formData[key];

                    // Convert empty Quill content to null
                    if ((key === "content_2" || key === "content") && isQuillEmpty(value)) {
                        value = null;
                    }

                    if (value !== null) {
                        formDataToSend.append(key, value);
                    }
                }
            });

            bodyImages.forEach((img, i) => {
                const index = i + 1;
                if (img.file) {
                    formDataToSend.append(`image_path_body_${index}`, img.file);
                    formDataToSend.append(`image_path_body_${index}_caption`, img.caption);
                    formDataToSend.append(`image_${index}_portrait`, img.portrait ? "1" : "0");
                }
            });

            const response = await createNewsPost(formDataToSend);

            if (response.data?.success) {
                setSuccessMessage(isDraft ? "Draft saved successfully!" : "News post created successfully!");
                navigate('/dashboard/news/list', { state: { user } });
            } else {
                setErrors([response.data?.message || "An unexpected error occurred"]);
            }
        } catch (error: any) {
            console.error("Error submitting form:", error);
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                setErrors(errorMessages as string[]);
            } else {
                setErrors(["An error occurred while submitting the form"]);
            }
        }
    };

    const addPortraitPair = () => {
        if (bodyImages.length + 2 <= 27) {
            setBodyImages(prev => [
                ...prev,
                { id: Date.now(), file: null, caption: "", portrait: true },
                { id: Date.now() + 1, file: null, caption: "", portrait: true }
            ]);
        }
    };

    const addLandscapeImage = () => {
        if (bodyImages.length + 1 <= 27) {
            setBodyImages(prev => [
                ...prev,
                { id: Date.now(), file: null, caption: "", portrait: false }
            ]);
        }
    };

    const updateImageField = (id: number, file: File | null, caption?: string, portrait?: boolean) => {
        setBodyImages(prev =>
            prev.map(img => {
                if (img.id === id) {
                    return {
                        ...img,
                        file: file ?? img.file,
                        caption: caption !== undefined ? caption : img.caption,
                        portrait: portrait !== undefined ? portrait : img.portrait
                    };
                }
                return img;
            })
        );
    };

    const removeImageField = (id: number) => {
        setBodyImages(prev => prev.filter(img => img.id !== id));
    };

    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />

            <div className={`main-admin-page ${isCollapsed ? "collapsed" : "expanded"}`}>
                <div className="row">
                    <div className="col-md-12 col-sm-12 col-xs-12">
                        <div className="x_panel">
                            <div className="x_title"><h2>Create News Post</h2></div>
                            <div className="notification-box">
                                {errors.length > 0 && <div className="alert alert-danger">
                                    <ul>{errors.map((error, i) => <li key={i}>{error}</li>)}</ul>
                                </div>}
                                {successMessage && <div className="alert alert-success"><i className="fa fa-check"></i>{successMessage}</div>}
                            </div>
                            <div className="x_content">
                                <form onSubmit={handleSubmit} encType="multipart/form-data" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                                    {/* --- LEFT / RIGHT COLUMN --- */}
                                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                        {/* LEFT COLUMN */}
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                            {/* Cover Image */}
                                            <div>
                                                <label>Cover Image</label><br />
                                                <input type="file" name="image" className="form-control" onChange={handleInputChange} />
                                                {formData.image && (
                                                    <img src={URL.createObjectURL(formData.image)} alt="Cover Thumbnail" style={{ width: '100%', marginTop: '10px', objectFit: 'cover', borderRadius: '5px' }} />
                                                )}
                                            </div>

                                            {/* Video */}
                                            <div>
                                                <label>Video Embed</label>
                                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <input type="text" name="video" className="form-control" placeholder="Enter video URL (iframe src)" value={formData.video} onChange={handleInputChange} />
                                                    <div style={{ cursor: 'help', fontSize: '20px', color: '#ffd700', position: 'relative' }}
                                                         onMouseEnter={() => setShowIframeHelp(true)}
                                                         onMouseLeave={() => setShowIframeHelp(false)}
                                                    >
                                                        💡
                                                        {showIframeHelp && (
                                                            <div style={{ position: 'absolute', top: 'calc(100% + 5px)', right: 0, background: 'white', border: '1px solid black', padding: '10px', width: '200px', color: 'black', zIndex: 1000 }}>
                                                                <h4 style={{ marginTop: 0 }}>iframe Instructions</h4>
                                                                <p>1. Go to Youtube</p>
                                                                <p>2. Click Share → Embed</p>
                                                                <p>3. Paste the Link (https://youtube.com/embed/link123)</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Quote */}
                                            <div>
                                                <label>Quote</label>
                                                <ReactQuill value={formData.content_2} onChange={(val: string) => handleQuillChange(val, 'content_2')} modules={modules} theme="snow" />
                                            </div>
                                        </div>

                                        {/* RIGHT COLUMN */}
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                            <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="form-control" placeholder="Title" required />
                                            <input type="date" name="year_posted" value={formData.year_posted} onChange={handleInputChange} className="form-control" required />
                                            <textarea name="subtitle" value={formData.subtitle} onChange={handleInputChange} className="form-control" placeholder="Subtitle" required />
                                            <div>
                                                <label>Content</label>
                                                <ReactQuill value={formData.content} onChange={(val: string) => handleQuillChange(val, 'content')} modules={modules} theme="snow" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- BODY IMAGES --- */}
                                    <div>
                                        <div style={{ display: 'flex', gap: '10px', margin: '15px 0' }}>
                                            <button type="button" className="btn btn-primary" onClick={addPortraitPair} disabled={bodyImages.length >= 27}>Add Portrait Image Pair</button>
                                            <button type="button" className="btn btn-secondary" onClick={addLandscapeImage} disabled={bodyImages.length >= 27}>Add Landscape Image</button>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                                            {bodyImages.map((img, index) => (
                                                <div key={img.id} style={{ border: '1px solid #ccc', padding: '10px', position: 'relative' }}>
                                                    <label>Body Image {index + 1}</label>
                                                    <input type="file" className="form-control" onChange={(e) => updateImageField(img.id, e.target.files?.[0] || null)} />
                                                    <input type="text" className="form-control" placeholder="Caption" value={img.caption} onChange={(e) => updateImageField(img.id, null, e.target.value)} />
                                                    <label style={{ display: 'block', marginTop: '5px' }}>
                                                        <input type="checkbox" checked={img.portrait} onChange={(e) => updateImageField(img.id, null, undefined, e.target.checked)} style={{ marginRight: '5px' }} /> Portrait
                                                    </label>
                                                    <button type="button" className="btn btn-danger btn-sm" style={{ position: 'absolute', top: 5, right: 5 }} onClick={() => removeImageField(img.id)}>Remove</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* --- SUBMIT BUTTONS --- */}
                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                                        <button type="button" className="btn btn-info" style={{ width: '10%' }} onClick={(e) => handleSubmit(e as any, true)}>Save Draft</button>
                                        <button type="submit" className="btn btn-success" style={{ width: '10%' }}>Submit</button>
                                    </div>

                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CreateNewsPost;
